import React, { useEffect, useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import ReactGA from 'react-ga'
import { useMeasure } from 'react-use'
import { UseMeasureRef } from 'react-use/lib/useMeasure'
import Popover from '@material-ui/core/Popover'
import { ClassNameMap } from '@material-ui/core/styles/withStyles'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import moment from 'moment'

import StyledButton from '../StyledButton'

import 'react-datepicker/dist/react-datepicker.css'
import './DateRangePicker.css'

enum PlaceholderMode {
  Months,
  Range,
}

interface AnchorProps {
  handleClick?: (event: React.MouseEvent) => void
  isPopoverOpen: boolean
  placeholderText: string
  anchorRef: UseMeasureRef<HTMLDivElement>
  styles?: ClassNameMap
}

const Anchor: React.FC<AnchorProps> = ({ anchorRef, handleClick, isPopoverOpen, placeholderText, styles }) => (
  <div
    id="date-select"
    data-testid="date-select"
    className={[styles?.selectInput, 'MuiInputBase-root', 'MuiOutlinedInput-root', 'MuiInputBase-formControl'].join(
      ' ',
    )}
    ref={anchorRef}
  >
    <button
      id="date-select-anchor-button"
      type="button"
      data-testid="date-select-anchor-button"
      className="MuiSelect-root MuiSelect-select MuiSelect-selectMenu MuiSelect-outlined MuiInputBase-input MuiOutlinedInput-input"
      onClick={handleClick}
    >
      {placeholderText}
    </button>
    <input aria-hidden="true" className="MuiSelect-nativeInput" tabIndex={-1} />

    <KeyboardArrowDownIcon
      className={['MuiSelect-icon', 'MuiSelect-iconOutlined', isPopoverOpen ? ' MuiSelect-iconOpen' : ''].join(' ')}
    />

    <fieldset aria-hidden="true" className="DateRangePickerFieldSet">
      <legend className="DateRangePickerFieldSetLegend">
        <span />
      </legend>
    </fieldset>
  </div>
)

export interface DateRangePickerProps {
  minDate?: Date
  maxDate?: Date
  selectedStartDate?: Date
  selectedEndDate?: Date
  onChange: (start: Date, end: Date) => void
  placeholderText?: string
  preselectMonths?: number[]
  styles?: ClassNameMap
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  minDate,
  maxDate,
  selectedStartDate = null,
  selectedEndDate = null,
  onChange,
  placeholderText = 'Select date',
  preselectMonths = [6, 12],
  styles,
}) => {
  const [anchorElement, setAnchorElement] = useState<Element | null>(null)
  const [anchorRef, { width }] = useMeasure<HTMLDivElement>()
  const isPopoverOpen = Boolean(anchorElement)

  const [localStartDate, setLocalStartDate] = useState<Date | null>(selectedStartDate)
  const [localEndDate, setLocalEndDate] = useState<Date | null>(selectedEndDate)
  const [placeholder, setPlaceholder] = useState(placeholderText)
  const placeholderDisplayMode = useRef<PlaceholderMode>(PlaceholderMode.Months)

  const closePopover = () => setTimeout(() => setAnchorElement(null), 0)

  const getPlaceholderValue = (start: Date, end: Date) => {
    if (isPopoverOpen) {
      return placeholderText
    }

    const startDateClone = moment(start).clone()
    const calculatedStartDate = moment(startDateClone).startOf('month')

    const endDateClone = moment(end).clone()
    const calculatedEndDate = moment(endDateClone).endOf('month').add(1, 'days')

    const diffMonthCount = calculatedEndDate.diff(calculatedStartDate, 'months')
    const isMultipleMonths = diffMonthCount !== 1

    switch (placeholderDisplayMode.current) {
      case PlaceholderMode.Months: {
        return `${diffMonthCount} month${isMultipleMonths ? 's' : ''}`
      }
      case PlaceholderMode.Range: {
        if (diffMonthCount > 1) {
          return `${calculatedStartDate.format('MMM YYYY')} - ${endDateClone.format('MMM YYYY')}`
        }
        return `${startDateClone.format('MMM YYYY')}`
      }
      default: {
        return ''
      }
    }
  }

  // NOTE: `dates` will contain either a start date only, or both start and end dates
  const onChangeHandler = (dates: [Date, Date]) => {
    const [start, end] = dates
    placeholderDisplayMode.current = PlaceholderMode.Range
    const calculatedEndDate = end ? moment(end).endOf('month').toDate() : end
    setLocalStartDate(start)
    setLocalEndDate(calculatedEndDate)

    if (start && calculatedEndDate) {
      onChange && onChange(start, calculatedEndDate)
      closePopover()

      const startStr = moment(start).format('DD/MM/YYYY')
      const endStr = moment(calculatedEndDate).format('DD/MM/YYYY')

      ReactGA.event({
        category: 'Refinance Index',
        action: 'Date change',
        label: `Dates selected: ${startStr} to ${endStr}`,
      })
    }
  }

  const onClickHandler = (event: React.MouseEvent) => {
    setAnchorElement(event.currentTarget)
  }

  const onFixedMonthSelectionHandler = (monthCount: number) => {
    placeholderDisplayMode.current = PlaceholderMode.Months
    const today = new Date()
    const calculatedStartDate = moment(today)
      .subtract(monthCount - 1, 'months')
      .startOf('month')
      .toDate()

    setLocalStartDate(calculatedStartDate)
    setLocalEndDate(today)

    onChange && onChange(calculatedStartDate, today)
    closePopover()
  }

  useEffect(() => {
    localStartDate && localEndDate && setPlaceholder(getPlaceholderValue(localStartDate, localEndDate))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStartDate, localEndDate, isPopoverOpen])

  return (
    <>
      <Anchor
        anchorRef={anchorRef}
        handleClick={onClickHandler}
        isPopoverOpen={isPopoverOpen}
        placeholderText={placeholder}
        styles={styles}
      />
      <Popover
        id="date-select-popover"
        data-testid="date-select-popover"
        open={isPopoverOpen}
        anchorEl={anchorElement}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          elevation: 3,
          square: false,
          style: { width, marginTop: '4px' },
        }}
      >
        <div style={{ margin: '0 8px' }}>
          {preselectMonths.map((pm) => (
            <StyledButton
              key={`date-select-${pm}-months-button`}
              testId={`date-select-${pm}-months-button`}
              handleClick={() => onFixedMonthSelectionHandler(pm)}
              height={48}
              style={{ width: '100%', padding: '16px 24px', margin: '4px 0' }}
            >{`Last ${pm} months`}</StyledButton>
          ))}

          <div style={{ display: 'grid', alignItems: 'center' }}>
            <DatePicker
              endDate={localEndDate}
              inline
              minDate={minDate}
              maxDate={maxDate}
              onChange={onChangeHandler}
              selected={localStartDate}
              selectsRange
              showMonthYearPicker
              startDate={localStartDate}
            />
          </div>
        </div>
      </Popover>
    </>
  )
}
